import React, { useState, useEffect } from 'react'
import { getHighScore } from '../lib/game'
import { useFullscreen } from '../lib/fullscreen'

const FLOATING_NUMBERS = [
  { num: '12', x: '8%', y: '15%', size: 'text-3xl', delay: '0s', opacity: 0.25 },
  { num: '36', x: '18%', y: '70%', size: 'text-2xl', delay: '1.2s', opacity: 0.2 },
  { num: '÷', x: '25%', y: '35%', size: 'text-4xl', delay: '0.5s', opacity: 0.18 },
  { num: '48', x: '35%', y: '80%', size: 'text-xl', delay: '2s', opacity: 0.22 },
  { num: '×', x: '42%', y: '20%', size: 'text-5xl', delay: '1s', opacity: 0.15 },
  { num: '24', x: '55%', y: '75%', size: 'text-3xl', delay: '0.8s', opacity: 0.2 },
  { num: '60', x: '68%', y: '25%', size: 'text-2xl', delay: '1.5s', opacity: 0.25 },
  { num: '∞', x: '75%', y: '65%', size: 'text-4xl', delay: '0.3s', opacity: 0.18 },
  { num: '15', x: '82%', y: '40%', size: 'text-3xl', delay: '1.8s', opacity: 0.22 },
  { num: '=', x: '88%', y: '78%', size: 'text-5xl', delay: '0.7s', opacity: 0.15 },
  { num: '9', x: '5%', y: '50%', size: 'text-2xl', delay: '2.2s', opacity: 0.2 },
  { num: '72', x: '92%', y: '15%', size: 'text-xl', delay: '1.1s', opacity: 0.22 },
]

function ModeCard({
  id, icon, title, subtitle, description,
  gradient, borderColor, shadowColor, glowColor, delay, highScore, onClick
}) {
  const [hovered, setHovered] = useState(false)

  return (
    <button
      id={id}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative group btn-neon w-full md:w-[280px] max-w-[320px] md:max-w-[280px] text-left cursor-pointer"
      style={{
        animationDelay: delay,
        animation: `slide-up 0.7s ease-out ${delay} both`,
      }}
    >
      <div
        className="relative overflow-hidden rounded-3xl transition-all duration-500 w-full flex flex-col items-center justify-center p-6 sm:p-8 gap-3 sm:gap-4 min-h-[260px] sm:min-h-[320px]"
        style={{
          background: hovered ? gradient : 'rgba(255,255,255,0.04)',
          border: `1.5px solid ${hovered ? borderColor : 'rgba(255,255,255,0.1)'}`,
          boxShadow: hovered
            ? `0 0 40px ${shadowColor}, 0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)`
            : '0 8px 32px rgba(0,0,0,0.3)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          transform: hovered ? 'translateY(-6px) scale(1.02)' : 'translateY(0) scale(1)',
        }}
      >
        {/* Animated border glow */}
        {hovered && (
          <div
            className="absolute inset-0 rounded-3xl pointer-events-none"
            style={{
              background: `radial-gradient(ellipse at 50% 0%, ${glowColor}33 0%, transparent 60%)`,
            }}
          />
        )}

        {/* Icon bubble */}
        <div
          className="relative flex items-center justify-center rounded-full transition-all duration-500 w-20 h-20 sm:w-24 sm:h-24 text-4xl sm:text-5xl"
          style={{
            background: hovered
              ? `radial-gradient(circle, ${glowColor}66, ${glowColor}22)`
              : 'rgba(255,255,255,0.06)',
            border: `2px solid ${hovered ? glowColor + '88' : 'rgba(255,255,255,0.1)'}`,
            boxShadow: hovered ? `0 0 30px ${glowColor}66` : 'none',
            transform: hovered ? 'scale(1.08) rotate(-3deg)' : 'scale(1)',
          }}
        >
          <span>{icon}</span>
        </div>

        {/* Title */}
        <div className="text-center">
          <div
            className="font-bold tracking-wide transition-all duration-300 text-2xl sm:text-3xl"
            style={{
              color: hovered ? '#fff' : 'rgba(255,255,255,0.85)',
              textShadow: hovered ? `0 0 20px ${glowColor}` : 'none',
              fontFamily: 'var(--font-kanit)',
            }}
          >
            {title}
          </div>
          <div
            className="text-xs sm:text-sm font-medium mt-1 transition-all duration-300"
            style={{
              color: hovered ? glowColor : 'rgba(255,255,255,0.5)',
              letterSpacing: '0.05em',
            }}
          >
            {subtitle}
          </div>
        </div>

        {/* Description */}
        <p
          className="text-center text-xs sm:text-sm leading-relaxed transition-all duration-300 max-w-[220px]"
          style={{
            color: hovered ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
            fontFamily: 'var(--font-kanit)',
          }}
        >
          {description}
        </p>

        {/* High Score Badge */}
        {highScore > 0 && (
          <div
            className="text-[0.7rem] font-bold px-3 py-1 rounded-full flex items-center gap-1"
            style={{
              background: 'rgba(245,158,11,0.15)',
              border: '1px solid rgba(245,158,11,0.3)',
              color: '#f59e0b',
            }}
          >
            🏆 High Score: {highScore} pts
          </div>
        )}

        {/* Play button indicator */}
        <div className="flex items-center gap-2 mt-1 sm:mt-2 transition-all duration-400 opacity-90 sm:opacity-0 group-hover:opacity-100 transform translate-y-0 sm:translate-y-2 sm:group-hover:translate-y-0">
          <span
            className="text-xs sm:text-sm font-semibold px-4 py-1.5 sm:px-5 sm:py-2 rounded-full"
            style={{
              background: gradient,
              color: '#fff',
              border: `1px solid ${glowColor}66`,
              boxShadow: `0 0 15px ${glowColor}44`,
            }}
          >
            เล่นเลย →
          </span>
        </div>
      </div>
    </button>
  )
}

export default function LandingPage({ onSelectGame }) {
  const [mounted, setMounted] = useState(false)
  const [topic, setTopic] = useState('mix')
  const [difficulty, setDifficulty] = useState('medium')
  const [highScore1p, setHighScore1p] = useState(0)
  const [highScore2p, setHighScore2p] = useState(0)
  const { fullscreen, toggleFullscreen } = useFullscreen()

  useEffect(() => {
    setMounted(true)
    setHighScore1p(getHighScore('1p'))
    setHighScore2p(getHighScore('2p'))
  }, [])

  const handleMode = (mode) => {
    if (onSelectGame) {
      onSelectGame({ mode, topic, difficulty })
    }
  }

  return (
    <main
      className="relative w-full min-h-screen overflow-y-auto overflow-x-hidden flex flex-col items-center justify-center py-8 px-4 sm:px-6 select-none"
      style={{ fontFamily: 'var(--font-kanit)' }}
    >
      {/* Top right Fullscreen toggle button */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={toggleFullscreen}
          className="glass-card px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-white/10 transition-all cursor-pointer border border-white/20 shadow-lg"
          style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(10px)' }}
        >
          <span>{fullscreen ? '📉' : '⛶'}</span>
          <span>{fullscreen ? 'ออกจากเต็มจอ' : 'เต็มจอ (Fullscreen)'}</span>
        </button>
      </div>

      {/* Background image */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <img
          src="/game_bg.jpg"
          alt="background"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(10,0,21,0.85) 0%, rgba(15,0,37,0.8) 50%, rgba(10,0,21,0.88) 100%)',
          }}
        />
      </div>

      {/* Floating math symbols */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {mounted && FLOATING_NUMBERS.map((item, i) => (
          <div
            key={i}
            className="absolute pointer-events-none select-none font-bold"
            style={{
              left: item.x,
              top: item.y,
              fontSize: item.size === 'text-2xl' ? '1.5rem'
                : item.size === 'text-3xl' ? '1.875rem'
                : item.size === 'text-4xl' ? '2.25rem'
                : item.size === 'text-5xl' ? '3rem' : '1.25rem',
              opacity: item.opacity,
              color: i % 3 === 0 ? '#a78bfa' : i % 3 === 1 ? '#06b6d4' : '#f59e0b',
              animation: i % 2 === 0
                ? `float ${6 + (i % 4)}s ease-in-out ${item.delay} infinite`
                : `floatReverse ${7 + (i % 3)}s ease-in-out ${item.delay} infinite`,
            }}
          >
            {item.num}
          </div>
        ))}
      </div>

      {/* Subtle grid */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(124, 58, 237, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(124, 58, 237, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Main content container */}
      <div className="relative z-10 flex flex-col items-center gap-5 sm:gap-6 w-full max-w-4xl mx-auto my-auto py-4">
        {/* Badge */}
        <div
          className="animate-slide-up"
          style={{ animationDelay: '0s', animationFillMode: 'both' }}
        >
          <span
            className="text-xs font-semibold px-4 py-1.5 rounded-full tracking-widest uppercase"
            style={{
              background: 'rgba(124, 58, 237, 0.2)',
              border: '1px solid rgba(124, 58, 237, 0.4)',
              color: '#c4b5fd',
              letterSpacing: '0.15em',
            }}
          >
            ⚡ 60-SECOND MATH SPEED RUSH
          </span>
        </div>

        {/* Main title */}
        <div
          className="text-center animate-slide-up"
          style={{ animationDelay: '0.15s', animationFillMode: 'both' }}
        >
          <h1
            className="font-extrabold leading-tight"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)' }}
          >
            <span className="text-shimmer">ห.ร.ม.</span>
            <span className="text-white mx-2 sm:mx-3 opacity-40">&amp;</span>
            <span className="text-shimmer">ค.ร.น.</span>
          </h1>
          <p
            className="text-xs sm:text-sm font-medium mt-1 text-emerald-400 font-mono"
          >
            ⏱️ จำกัดเวลา 1 นาที — ตอบถูก +2 คะแนน / ตอบผิด -1 คะแนน
          </p>
        </div>

        {/* Topic & Difficulty Selection Row */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 w-full animate-slide-up" style={{ animationDelay: '0.25s', animationFillMode: 'both' }}>
          {/* Topic selector */}
          <div className="flex flex-col items-center gap-1.5 w-full sm:w-auto">
            <span className="text-xs font-medium tracking-wide text-white/60">
              หมวดโจทย์:
            </span>
            <div className="flex justify-center gap-1 sm:gap-1.5 p-1.5 rounded-2xl glass-card w-full sm:w-auto" style={{ border: '1px solid rgba(255,255,255,0.12)' }}>
              {[
                { id: 'hrm', label: '🟣 ห.ร.ม.', color: 'rgba(139,92,246,0.4)', borderColor: '#a78bfa' },
                { id: 'krn', label: '🔵 ค.ร.น.', color: 'rgba(6,182,212,0.4)', borderColor: '#06b6d4' },
                { id: 'mix', label: '⚡ สลับ', color: 'rgba(245,158,11,0.4)', borderColor: '#f59e0b' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setTopic(t.id)}
                  className="flex-1 sm:flex-initial cursor-pointer"
                  style={{
                    padding: '6px 12px',
                    borderRadius: 10,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    transition: 'all 0.25s ease',
                    background: topic === t.id ? t.color : 'transparent',
                    border: `1.5px solid ${topic === t.id ? t.borderColor : 'transparent'}`,
                    color: topic === t.id ? '#fff' : 'rgba(255,255,255,0.5)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty selector */}
          <div className="flex flex-col items-center gap-1.5 w-full sm:w-auto">
            <span className="text-xs font-medium tracking-wide text-white/60">
              ระดับความยาก:
            </span>
            <div className="flex justify-center gap-1 sm:gap-1.5 p-1.5 rounded-2xl glass-card w-full sm:w-auto" style={{ border: '1px solid rgba(255,255,255,0.12)' }}>
              {[
                { id: 'easy', label: '🌱 ง่าย', color: 'rgba(16,185,129,0.35)', borderColor: '#10b981' },
                { id: 'medium', label: '⭐ ปานกลาง', color: 'rgba(245,158,11,0.35)', borderColor: '#f59e0b' },
                { id: 'hard', label: '🔥 ยาก', color: 'rgba(239,68,68,0.35)', borderColor: '#ef4444' },
              ].map(d => (
                <button
                  key={d.id}
                  onClick={() => setDifficulty(d.id)}
                  className="flex-1 sm:flex-initial cursor-pointer"
                  style={{
                    padding: '6px 12px',
                    borderRadius: 10,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    transition: 'all 0.25s ease',
                    background: difficulty === d.id ? d.color : 'transparent',
                    border: `1.5px solid ${difficulty === d.id ? d.borderColor : 'transparent'}`,
                    color: difficulty === d.id ? '#fff' : 'rgba(255,255,255,0.5)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mode selection */}
        <div
          className="animate-slide-up w-full flex flex-col items-center"
          style={{ animationDelay: '0.35s', animationFillMode: 'both' }}
        >
          <p
            className="text-center text-xs font-medium mb-3 tracking-wide"
            style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}
          >
            — เลือกผู้เล่น —
          </p>

          <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-center md:items-stretch justify-center w-full max-w-sm md:max-w-none">
            <ModeCard
              id="btn-1player"
              icon="🧑‍🚀"
              title="1 Player"
              subtitle="โหมดเดี่ยว 1 นาที"
              description="ตอบโจทย์ให้ได้มากที่สุดใน 1 นาที สะสมคะแนนและทำสถิติสูงสุด!"
              gradient="linear-gradient(135deg, rgba(124, 58, 237, 0.35), rgba(139, 92, 246, 0.2))"
              borderColor="rgba(139, 92, 246, 0.6)"
              shadowColor="rgba(124, 58, 237, 0.4)"
              glowColor="#7c3aed"
              delay="0.4s"
              highScore={highScore1p}
              onClick={() => handleMode('1p')}
            />

            {/* VS Divider */}
            <div className="flex md:flex-col flex-row items-center justify-center gap-3 select-none w-full md:w-auto my-1 md:my-0">
              <div className="hidden md:block w-[1px] flex-1 bg-gradient-to-b from-transparent via-white/15 to-transparent" />
              <div className="block md:hidden h-[1px] flex-1 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
              <span
                className="font-black text-lg md:text-xl px-2"
                style={{
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '0.05em',
                  fontFamily: 'var(--font-outfit)',
                }}
              >
                VS
              </span>
              <div className="hidden md:block w-[1px] flex-1 bg-gradient-to-b from-transparent via-white/15 to-transparent" />
              <div className="block md:hidden h-[1px] flex-1 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
            </div>

            <ModeCard
              id="btn-2players"
              icon="⚔️"
              title="2 Players"
              subtitle="ดวลสปีด 1 นาที"
              description="แข่งพร้อมกันใน 1 นาที ใครทำคะแนนได้มากกว่ากัน!"
              gradient="linear-gradient(135deg, rgba(6, 182, 212, 0.35), rgba(20, 184, 166, 0.2))"
              borderColor="rgba(6, 182, 212, 0.6)"
              shadowColor="rgba(6, 182, 212, 0.4)"
              glowColor="#06b6d4"
              delay="0.55s"
              highScore={highScore2p}
              onClick={() => handleMode('2p')}
            />
          </div>
        </div>

        {/* Footer hint */}
        <div
          className="animate-slide-up text-center mt-2"
          style={{ animationDelay: '0.7s', animationFillMode: 'both' }}
        >
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
            ห.ร.ม. = หารร่วมมาก (GCD) &nbsp;·&nbsp; ค.ร.น. = คูณร่วมน้อย (LCM)
          </p>
        </div>
      </div>
    </main>
  )
}
